<template>
  <section class="products-demo-section">
    <div class="demo-container">
      <div class="form-container">
        <!-- Título y descripción dentro del contenedor -->
        <div class="form-header">
          <h2 class="demo-title">
            ¿Quieres ver cómo <span class="highlight">FourinTrack</span> puede mejorar tu
            mantenimiento?
          </h2>
          <p class="demo-description">
            Agenda una demo gratuita y personalizada. Te mostraremos cómo adaptar la plataforma a tu
            negocio, paso a paso.
          </p>
        </div>

        <!-- Formulario -->
        <form @submit.prevent="handleSubmit" class="demo-form">
          <div class="form-row">
            <!-- Columna izquierda -->
            <div class="form-column">
              <div class="form-group">
                <label for="nombre">¿Como te llamas?</label>
                <input
                  type="text"
                  id="nombre"
                  v-model="formData.nombre"
                  required
                  placeholder="Tu nombre"
                />
              </div>

              <div class="form-group">
                <label for="email">¿A que correo enviamos la invitacion?</label>
                <input
                  type="email"
                  id="email"
                  v-model="formData.email"
                  required
                  placeholder="tu@tudomino.com"
                />
              </div>
            </div>

            <!-- Columna derecha -->
            <div class="form-column">
              <div class="form-group">
                <label for="empresa">¿Donde trabajas?</label>
                <input
                  type="text"
                  id="empresa"
                  v-model="formData.empresa"
                  required
                  placeholder="Nombre de tu empresa"
                />
              </div>

              <div class="form-group">
                <label for="telefono">¿Un telefono de contacto?</label>
                <input
                  type="tel"
                  id="telefono"
                  v-model="formData.telefono"
                  placeholder="+57 300 123 4567"
                />
              </div>
            </div>
          </div>

          <button type="submit" class="submit-button" :disabled="isSubmitting">
            <span class="material-symbols-outlined"> calendar_today </span>
            <span v-if="!isSubmitting">Agendar mi demo de fourinTrack gratuita</span>
            <span v-else>Enviando...</span>
            <div v-if="isSubmitting" class="loading-spinner"></div>
          </button>

          <!-- Mensaje de estado -->
          <div
            v-if="submitMessage"
            class="submit-message"
            :class="{
              success: !submitMessage.includes('error') && !submitMessage.includes('Error'),
            }"
          >
            {{ submitMessage }}
          </div>

          <!-- Iconos de beneficios -->
          <div class="benefits">
            <div class="benefit-item">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <rect x="14" y="14" width="7" height="7" rx="1" />
              </svg>
              <span>Demo personalizada</span>
            </div>
            <div class="benefit-item">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
              </svg>
              <span>Periodo de prueba gratuita</span>
            </div>
            <div class="benefit-item">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              <span>Soporte incluido</span>
            </div>
          </div>
        </form>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()

const formData = ref({
  nombre: '',
  empresa: '',
  email: '',
  telefono: '',
  empleados: '',
  mensaje: '',
})

const isSubmitting = ref(false)
const submitMessage = ref('')

const handleSubmit = async () => {
  isSubmitting.value = true
  submitMessage.value = ''

  try {
    // Usar la función helper de Supabase
    const { submitDemoRequest } = await import('../lib/supabase')

    const data = await submitDemoRequest(formData.value)
    console.log('Solicitud guardada exitosamente:', data)

    // Redirigir a página de agradecimiento después de 1 segundo
    // Esto permite que el usuario vea el mensaje de éxito brevemente
    setTimeout(() => {
      // Pasar el ID de la solicitud como query param para tracking
      const requestId = data?.[0]?.id || `demo_${Date.now()}`
      router.push({
        name: 'thank-you-demo',
        query: { id: requestId },
      })
    }, 1000)
  } catch (error) {
    console.error('Error:', error)
    submitMessage.value = 'Hubo un error enviando tu solicitud. Por favor intenta de nuevo.'
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
.products-demo-section {
  padding: 60px 20px;
  background: #ffffff;
  min-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
  border-top: 2px solid;
  border-image: linear-gradient(to right, #ffffff, #e2e8f0, #ffffff) 1;
  border-bottom: 2px solid;
}

.demo-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: center;
}

.form-container {
  background: #ffffff;
  border-radius: 24px;
  padding: 50px;
  width: 100%;
  max-width: 1000px;
}

.form-header {
  margin-bottom: 40px;
}

.demo-title {
  font-size: clamp(1.8rem, 3vw, 2.5rem);
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 20px;
  letter-spacing: -0.02em;
  color: #000000;
}

.demo-title .highlight {
  color: #129bef;
  font-weight: 700;
}

.demo-description {
  font-size: 16px;
  line-height: 1.6;
  color: #4b5563;
  margin: 0;
}

.demo-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.form-column {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  color: #000000;
  font-weight: 600;
  font-size: 14px;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #ffffff;
  color: #000000;
  font-size: 16px;
  transition: all 0.3s ease;
}

.form-group input::placeholder,
.form-group textarea::placeholder {
  color: #9ca3af;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #129bef;
  box-shadow: 0 0 0 3px rgba(18, 155, 239, 0.1);
}

.submit-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #129bef;
  color: #ffffff;
  border: none;
  padding: 18px 32px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 10px;
  width: 100%;
}

.submit-button:hover {
  background: #0ea5e9;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(18, 155, 239, 0.3);
}

.submit-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.submit-button:disabled:hover {
  transform: none;
  box-shadow: none;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #ffffff;
  border-top: 2px solid transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.submit-message {
  margin-top: 15px;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #dc2626;
}

.submit-message.success {
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  color: #16a34a;
}

.benefits {
  display: flex;
  justify-content: center;
  gap: 32px;
  margin-top: 30px;
  padding-top: 30px;
  border-top: 1px solid #e5e7eb;
}

.benefit-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: #4b5563;
  font-size: 14px;
  text-align: center;
}

.benefit-item svg {
  color: #129bef;
  margin-bottom: 4px;
}

.benefit-item span {
  font-weight: 500;
}

/* Responsive design */
@media (max-width: 968px) {
  .form-row {
    grid-template-columns: 1fr;
  }

  .benefits {
    flex-direction: column;
    gap: 20px;
  }
}

@media (max-width: 768px) {
  .products-demo-section {
    padding: 40px 20px;
  }

  .form-container {
    padding: 40px 30px;
  }

  .demo-title {
    font-size: 2rem;
  }

  .demo-description {
    font-size: 15px;
  }
}

@media (max-width: 480px) {
  .form-container {
    padding: 30px 20px;
    border-radius: 16px;
  }

  .demo-title {
    font-size: 1.75rem;
  }

  .submit-button {
    padding: 16px 28px;
    font-size: 14px;
  }

  .benefits {
    gap: 16px;
  }

  .benefit-item {
    font-size: 13px;
  }
}
</style>
